import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Newspaper, Loader2 } from "lucide-react";
import { getMyNewsletter, putMyNewsletter, errMsg } from "@/lib/applyApi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const fmt = (v) => (v ? new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

function StatusText({ n }) {
  if (n.subscribed) return <span className="text-emerald-800">Subscribed{n.consent_at ? ` since ${fmt(n.consent_at)}` : ""}</span>;
  if (n.status === "unsubscribed") return <span className="text-stone-700">Unsubscribed{n.opted_out_at ? ` on ${fmt(n.opted_out_at)}` : ""}</span>;
  return <span className="text-stone-700">Not subscribed</span>;
}

// Marketing newsletter consent only; application and contract notifications are separate and always sent.
export function NewsletterCard() {
  const [n, setN] = useState(null);
  const [want, setWant] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getMyNewsletter().then((d) => { setN(d); setWant(d.subscribed); }).catch(() => setN(false)); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const d = await putMyNewsletter(want);
      setN(d); setWant(d.subscribed);
      toast.success(d.subscribed ? "You're subscribed to the newsletter" : "You've been unsubscribed from the newsletter");
    } catch (e) { toast.error(errMsg(e, "Could not save newsletter preference")); } finally { setBusy(false); }
  };
  return (
    <section id="newsletter-card" data-testid="newsletter-card" className="bg-white border border-stone-200 rounded-xl p-4 mb-4 scroll-mt-20">
      <h2 className="font-semibold text-stone-900 flex items-center gap-2 mb-2"><Newspaper className="h-4 w-4 text-amber-800" /> Newsletter Subscription</h2>
      {n === null ? <Loader2 className="h-4 w-4 animate-spin text-stone-400" /> : n === false ? <p className="text-sm text-red-700">Couldn't load your newsletter preference.</p> : (
        <>
          <p data-testid="newsletter-status" className="text-sm font-semibold mb-3">Status: <StatusText n={n} /></p>
          <label className="flex items-start gap-2.5 text-sm text-stone-800 cursor-pointer">
            <Checkbox data-testid="newsletter-checkbox" checked={want} onCheckedChange={(v) => setWant(!!v)} className="mt-0.5 rounded-[4px]" />
            <span>{n.consent_text}</span>
          </label>
          <p className="text-xs text-stone-500 mt-2">You can unsubscribe at any time. Application, contract and sale updates are still sent either way.</p>
          <Button data-testid="newsletter-save" onClick={save} disabled={busy || want === n.subscribed} className="mt-3 h-10 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Save Newsletter Preference</Button>
        </>
      )}
    </section>
  );
}
